import { useCallback, useEffect, useRef, useState } from 'react'

/** エリア編集キャンバスの操作モード。select = 頂点編集 / pan = キャンバス移動 */
export type CanvasToolMode = 'select' | 'pan'

interface UseCanvasToolModeOptions {
  /** キーボードショートカット (Space / V / ⌘Z / Delete) を有効にするか */
  enabled?: boolean
  /** ⌘Z / Ctrl+Z 押下時のコールバック */
  onUndo?: () => void
  /** Delete / Backspace 押下時のコールバック（選択中頂点の削除） */
  onDeleteSelected?: () => void
}

/**
 * イベントの発生元がテキスト入力中の要素かどうか。
 * 入力中の Space / Delete / ⌘Z をキャンバス操作として奪わないためのガード。
 *
 * 注意: BUTTON はここに含めない。編集モードへの遷移は必ずボタンクリック
 * (レイヤー選択・ツールバー等) で行われ、クリック後はフォーカスがそのボタンに
 * 残るため、BUTTON を除外すると Space が実質常に無視されてしまう
 * (キャンバス側は mousedown で preventDefault するためフォーカスが移らない)。
 * キャンバス編集中は Space をパン操作に優先させる (ボタンは Enter で操作可能)。
 * VertexContextMenu の項目 (role="menuitem") のみキーボード操作を優先する。
 */
const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.getAttribute('role') === 'menuitem'
  )
}

/**
 * エリア編集キャンバスの select / pan モード管理フック。
 *
 * - `mode`: ツールバーで明示的に選んだモード
 * - `effectiveMode`: Space 押下中は一時的に 'pan' になる実効モード（押下解除で `mode` に復帰）
 * - V キーで select モードへ、⌘Z / Ctrl+Z で undo、Delete / Backspace で選択頂点の削除
 */
export const useCanvasToolMode = ({
  enabled = true,
  onUndo,
  onDeleteSelected,
}: UseCanvasToolModeOptions = {}) => {
  const [mode, setMode] = useState<CanvasToolMode>('select')
  const [isSpacePressed, setIsSpacePressed] = useState(false)
  // ハンドラをlistener再登録なしで最新に保つ
  const onUndoRef = useRef(onUndo)
  const onDeleteSelectedRef = useRef(onDeleteSelected)
  onUndoRef.current = onUndo
  onDeleteSelectedRef.current = onDeleteSelected

  useEffect(() => {
    if (!enabled) {
      setIsSpacePressed(false)
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTextEntryTarget(e.target)) return

      if (e.code === 'Space') {
        // preventDefault で「ページスクロール」と「フォーカス中ボタンの Space 再発火」を
        // 両方抑止しつつ、押下中のみ pan モードにする
        e.preventDefault()
        if (!e.repeat) {
          setIsSpacePressed(true)
        }
        return
      }
      if ((e.key === 'v' || e.key === 'V') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        setMode('select')
        return
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        e.preventDefault()
        onUndoRef.current?.()
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        onDeleteSelectedRef.current?.()
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false)
      }
    }

    // タブ切替等で keyup を取りこぼした時に押しっぱなし状態が残らないように
    const handleBlur = () => setIsSpacePressed(false)

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', handleBlur)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleBlur)
      setIsSpacePressed(false)
    }
  }, [enabled])

  const effectiveMode: CanvasToolMode = isSpacePressed ? 'pan' : mode

  const handleModeChange = useCallback((next: CanvasToolMode) => {
    setMode(next)
  }, [])

  return {
    mode,
    effectiveMode,
    setMode: handleModeChange,
    isSpacePressed,
  }
}
